import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTH_NAMES = ['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'];

function formatMonthRange(months) {
  if (!months || months.length === 0) return '';
  const sorted = [...months].sort((a, b) => a - b);
  if (sorted.length === 1) return MONTH_NAMES[sorted[0] - 1];
  return `${MONTH_NAMES[sorted[0] - 1]} a ${MONTH_NAMES[sorted[sorted.length - 1] - 1]}`;
}

function formatMonthListFull(months) {
  if (!months || months.length === 0) return '';
  const sorted = [...months].sort((a, b) => a - b);
  if (sorted.length <= 2) return sorted.map(m => MONTH_NAMES[m - 1]).join(' et ');
  return sorted.slice(0, -1).map(m => MONTH_NAMES[m - 1]).join(', ') + ' et ' + MONTH_NAMES[sorted[sorted.length - 1] - 1];
}

export function applySeasonPlaceholders(content, seasonConfig) {
  return content
    .replaceAll('{{SAISON_HAUTE}}', formatMonthRange(seasonConfig.haute))
    .replaceAll('{{SAISON_INTERMEDIAIRE}}', formatMonthListFull(seasonConfig.intermediaire))
    .replaceAll('{{SAISON_BASSE}}', formatMonthListFull(seasonConfig.basse));
}

const MD_COMPONENTS = {
  h2: (props) => <h2 className="font-['Playfair_Display'] text-xl md:text-2xl text-[#2C3E30] mt-10 mb-4 pb-2 border-b border-[#E5E5E0]" {...props} />,
  h3: (props) => <h3 className="font-semibold text-[#2C3E30] text-base mt-6 mb-2" {...props} />,
  p: (props) => <p className="text-sm text-[#454545] leading-relaxed mb-4" {...props} />,
  ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-1.5 text-sm text-[#454545]" {...props} />,
  ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-1.5 text-sm text-[#454545]" {...props} />,
  li: (props) => <li className="leading-relaxed" {...props} />,
  strong: (props) => <strong className="font-semibold text-[#2C3E30]" {...props} />,
  em: (props) => <em className="text-[#8A8A85]" {...props} />,
  a: (props) => <a className="text-[#8A9A5B] underline hover:text-[#7a8a4d]" {...props} />,
  table: (props) => (
    <div className="overflow-x-auto mb-6">
      <table className="w-full text-sm border border-[#E5E5E0] rounded-lg overflow-hidden" {...props} />
    </div>
  ),
  thead: (props) => <thead className="bg-[#2C3E30] text-[#F2F0E9]" {...props} />,
  th: (props) => <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wide" {...props} />,
  td: (props) => <td className="px-4 py-2.5 border-t border-[#E5E5E0] text-[#454545]" {...props} />,
  tr: (props) => <tr className="even:bg-[#FAFAF8]" {...props} />,
};

export default function LegalPage({ slug }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [seasonConfig, setSeasonConfig] = useState({ haute: [5, 6, 7, 8, 9], intermediaire: [4, 10], basse: [1, 2, 3, 11, 12] });

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/legal/${slug}`).then(r => setPage(r.data)).catch(() => setPage({ title: 'Page indisponible', content: '' }));
    axios.get(`${BACKEND_URL}/api/season-config`).then(r => { if (r.data) setSeasonConfig(r.data); }).catch(() => {});
  }, [slug]);

  const content = page ? applySeasonPlaceholders(page.content || '', seasonConfig) : '';

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="bg-[#2C3E30] text-[#F2F0E9] py-6">
        <div className="max-w-[900px] mx-auto px-6 flex items-center gap-4">
          <Button
            data-testid="legal-back-btn"
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-[#F2F0E9]/70 hover:text-[#F2F0E9] hover:bg-white/10 p-2 h-auto"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </Button>
          <h1 className="font-['Playfair_Display'] text-xl md:text-2xl">Le Domaine du Pilat</h1>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 py-12 md:py-16">
        {page && (
          <>
            <h1 data-testid="legal-page-title" className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#2C3E30] mb-2">
              {page.title}
            </h1>
            {page.updated_at && (
              <p className="text-xs text-[#8A8A85] mb-8" data-testid="legal-page-updated">
                Derniere modification : {new Date(page.updated_at).toLocaleDateString('fr-FR', { dateStyle: 'long' })}
              </p>
            )}
            <div data-testid="legal-page-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{content}</ReactMarkdown>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
